import { useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, RefreshCw, Search } from "lucide-react";
import { api } from "../api";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { ErrorState, PageHeader } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import { PostCard, PostLightbox, PostsView, type PostSource, type SocialPost } from "../components/competitors/SocialViews";
import type { Competitor } from "../competitors";

/**
 * Viral posts (Neil, 2026-10-09: "what about checking for viral content"): posts that got far
 * more views than usual, two ways. Across the niche searches TikTok and Instagram for what the
 * client does (utils/viralPosts.js, about 1 Scrape Creators credit each); From competitors reads
 * the accounts someone added on the Competitors page (its own copy of this tab was removed, and
 * its old link lands here). Every post can be read by the AI,
 * rewritten into Content review, or saved as a content idea.
 */

type Search = {
  id: number; query: string; time_window: "month" | "quarter"; platforms: string[]; found: number; viral: number; credits_used: number | null;
  notes: string[]; cursors: Record<string, unknown>; pages: number; triggered_by: "manual" | "weekly" | "shared"; created_at: string; next_refresh: string;
};
type ViralResponse = {
  client: { id: number; name: string }; ready: boolean; credits_left: number | null; can_change: boolean; refresh_days: number;
  defaults: { query: string }; search: Search | null; posts: SocialPost[]; counts: { total: number; viral: number };
  cached?: boolean; added?: number; added_viral?: number;
};
const VIRAL_SOURCE: PostSource = { kind: "viral", api: "/api/viral/posts" };
const PLATFORMS = [["tiktok", "TikTok"], ["instagram", "Instagram"]] as const;
const PAGE = 40;
const SORTS = { viral: "Most viral", newest: "Newest found", views: "Most views" } as const;

export default function ViralPosts() {
  const { clientId, client } = useClientSelection();
  return clientId ? <ViralPage key={clientId} clientId={clientId} clientName={client?.name || "this client"} /> : (
    <>
      <PageHeader title="Viral posts" />
      <NoClient />
    </>
  );
}

function ViralPage({ clientId, clientName }: { clientId: number; clientName: string }) {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "competitors" ? "competitors" : "niche";
  const setView = (v: "niche" | "competitors") => setParams((current) => {
    const next = new URLSearchParams(current);
    if (v === "niche") next.delete("view"); else next.set("view", v);
    return next;
  }, { replace: true });
  return (
    <div className="competitors-page viral-page">
      <PageHeader title="Viral posts" description={`Posts that got far more views than usual. Ideas to borrow for ${clientName}.`} />
      <div className="segmented-control competitors-views" role="group" aria-label="Where the posts come from">
        <button type="button" className={view === "niche" ? "active" : ""} aria-pressed={view === "niche"} onClick={() => setView("niche")}>Across the niche</button>
        <button type="button" className={view === "competitors" ? "active" : ""} aria-pressed={view === "competitors"} onClick={() => setView("competitors")}>From competitors</button>
      </div>
      {view === "niche" ? <NicheView clientId={clientId} /> : <CompetitorsView clientId={clientId} />}
    </div>
  );
}

function NicheView({ clientId }: { clientId: number }) {
  const { data, loading, error, reload, setData } = useApi<ViralResponse>(`/api/viral?client_id=${clientId}`);
  const [words, setWords] = useState<string | null>(null);
  const [windowChoice, setWindowChoice] = useState<"month" | "quarter" | null>(null);
  const [picked, setPicked] = useState<Record<string, boolean> | null>(null);
  const [busy, setBusy] = useState<"" | "search" | "more" | "refresh">("");
  const pending = useRef(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const [show, setShow] = useState<"viral" | "all">("viral");
  // Posts the AI marked as aimed at business owners, or off topic, stay out unless asked for.
  const [others, setOthers] = useState(false);
  const [platform, setPlatform] = useState("");
  const [sort, setSort] = useState<keyof typeof SORTS>("viral");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState<SocialPost | null>(null);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading && !data) return <p className="muted cs-pad">Loading…</p>;
  if (!data) return null;

  const search = data.search;
  const text = words ?? search?.query ?? data.defaults.query;
  const win = windowChoice ?? search?.time_window ?? "month";
  // Reopening a saved one-platform search must not silently add the other
  // platform (and another provider credit) to its next refresh.
  const chosen = picked ?? Object.fromEntries(PLATFORMS.map(([id]) => [id, !search || search.platforms.includes(id)]));
  const platforms = PLATFORMS.filter(([id]) => chosen[id]).map(([id]) => id);
  const hasMore = Boolean(search && Object.values(search.cursors || {}).some((v) => v !== null && v !== undefined));
  // Scrape Creators says 0 credits left (null means it did not say): every search, the weekly one
  // too (runDueViral), waits until someone adds credits. Posts already found keep working.
  const paused = data.ready && data.credits_left === 0;

  async function call(kind: "search" | "more" | "refresh", event?: FormEvent) {
    event?.preventDefault();
    if (pending.current || !data?.can_change || !data.ready || paused || (kind === "more" ? !hasMore : !text.trim() || !platforms.length)) return;
    pending.current = true;
    setBusy(kind); setErr(""); setDone("");
    try {
      const r = kind === "more"
        ? await api<ViralResponse>("/api/viral/more", { method: "POST", body: JSON.stringify({ client_id: clientId }) })
        : await api<ViralResponse>("/api/viral/search", { method: "POST", body: JSON.stringify({ client_id: clientId, query: text, window: win, platforms, force: kind === "refresh" }) });
      setData((prev) => (prev ? { ...prev, ...r } : r));
      setLimit(PAGE);
      setDone(r.cached ? "Already searched these words in the last 3 days, so this is that search (no credits used). Refresh now runs it again."
        : kind === "more" ? `${r.added ?? 0} new ${r.added === 1 ? "post" : "posts"} added to the library, ${r.added_viral ?? 0} of them viral.`
          : `${r.added ?? 0} new ${r.added === 1 ? "post" : "posts"} added to the library.`);
    } catch (e) { setErr(e instanceof Error ? e.message : "That did not work. Try again."); }
    finally { pending.current = false; setBusy(""); }
  }

  const forCustomers = (p: SocialPost) => p.audience !== "owners" && p.audience !== "other";
  const inView = (p: SocialPost) => (show === "all" || p.outlier) && (!platform || p.platform === platform);
  const otherCount = data.posts.filter((p) => inView(p) && !forCustomers(p)).length;
  const order: Record<keyof typeof SORTS, (a: SocialPost, b: SocialPost) => number> = {
    viral: () => 0, // the server's order: viral, near you, most above usual
    newest: (a, b) => Date.parse(b.first_seen_at) - Date.parse(a.first_seen_at) || (b.outlier?.ratio ?? 0) - (a.outlier?.ratio ?? 0),
    views: (a, b) => (b.views ?? 0) - (a.views ?? 0),
  };
  const posts = data.posts.filter((p) => inView(p) && (others || forCustomers(p))).sort(order[sort]);
  const credits = data.credits_left !== null ? `, ${data.credits_left.toLocaleString()} left` : "";
  const when = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" });

  return (
    <div className="cs-view">
      {data.can_change && (
        <form className="cs-find viral-find" onSubmit={(e) => void call("search", e)}>
          <label>Search for<input value={text} disabled={!!busy} placeholder="junk removal" maxLength={80} onChange={(e) => setWords(e.target.value)} /></label>
          {/* TikTok's own date filter for the next search (its "this-month" is the last 30 days). Instagram has none,
              and the library below keeps every post found before, so this does not filter the cards. */}
          <label>TikTok posted<select value={win} disabled={!!busy} onChange={(e) => setWindowChoice(e.target.value === "quarter" ? "quarter" : "month")}>
            <option value="month">Past month</option><option value="quarter">Past 3 months</option>
          </select></label>
          <div className="viral-on" role="group" aria-labelledby="viral-on">
            <span id="viral-on">Platforms</span>
            <span className="viral-on-boxes">
              {PLATFORMS.map(([id, name]) => (
                <label key={id}><input type="checkbox" disabled={!!busy} checked={chosen[id]} onChange={(e) => setPicked({ ...chosen, [id]: e.target.checked })} />{name}</label>
              ))}
            </span>
          </div>
          <button type="submit" className="button" disabled={!!busy || !data.ready || paused || !text.trim() || !platforms.length}><Search aria-hidden="true" />{busy === "search" ? "Searching…" : "Find viral posts"}</button>
        </form>
      )}
      {paused && (
        <p className="inline-notice warning" role="status">
          <span>Searching is paused because Scrape Creators has no credits left, so the weekly search will not run either. An admin can add credits at <a href="https://scrapecreators.com" target="_blank" rel="noreferrer">scrapecreators.com</a>. Posts already found still work.</span>
        </p>
      )}
      {data.can_change && (
        <p className="cs-note">
          {!data.ready
            ? <>Searching needs the Scrape Creators key. Add it in <Link to="/settings#integrations">Settings, Integrations</Link>.</>
            : paused
              ? "US accounts and English posts only. Every post found stays in this client's library. Instagram shows its top reels of all time for these words."
              : `US accounts and English posts only. About 1 Scrape Creators credit for each platform${credits}. Every post found stays in this client's library, and the search runs again by itself every week. Instagram shows its top reels of all time for these words.`}
        </p>
      )}
      {err && <p className="inline-notice danger" role="alert">{err}</p>}
      {done && <p className="inline-notice success" role="status"><span>{done}</span><button type="button" aria-label="Dismiss" onClick={() => setDone("")}>×</button></p>}
      {search && search.notes?.length > 0 && <p className="inline-notice warning">{search.notes.join(" ")}</p>}

      {!search ? (
        <div className="cs-empty">
          <strong>No search yet</strong>
          <p className="muted">{paused ? "Searches start again once Scrape Creators has credits." : data.can_change ? "Press Find viral posts. After that it refreshes by itself every week." : "The first search runs by itself within a week, or someone with exec access can run it now."}</p>
        </div>
      ) : (
        <>
          <div className="cs-toolbar">
            <span className="muted">Library: {data.counts.viral} viral of {data.counts.total} for "{search.query}". Last searched {when(search.created_at)}{paused ? "" : `, next ${when(search.next_refresh)}`}.</span>
            {data.can_change && data.ready && (
              <span className="cs-toolbar-end">
                <button type="button" className="button secondary" disabled={!!busy || paused || !hasMore} title={paused ? "Paused until Scrape Creators has credits." : hasMore ? "Reads the next page of results: about 1 credit for each platform." : "No more results for these words."} onClick={() => void call("more")}>
                  <Plus aria-hidden="true" />{busy === "more" ? "Finding more…" : "Find more"}
                </button>
                <button type="button" className="button secondary" disabled={!!busy || paused || !text.trim() || !platforms.length} title={paused ? "Paused until Scrape Creators has credits." : "Runs the search again now: about 1 credit for each platform."} onClick={() => void call("refresh")}>
                  <RefreshCw className={busy === "refresh" ? "spin" : ""} aria-hidden="true" />Refresh now
                </button>
              </span>
            )}
          </div>
          <div className="cs-toolbar">
            <div className="segmented-control compact" role="group" aria-label="Which posts">
              <button type="button" className={show === "viral" ? "active" : ""} aria-pressed={show === "viral"} onClick={() => { setShow("viral"); setLimit(PAGE); }}>Viral only</button>
              <button type="button" className={show === "all" ? "active" : ""} aria-pressed={show === "all"} onClick={() => { setShow("all"); setLimit(PAGE); }}>Everything found</button>
            </div>
            <select aria-label="Platform" value={platform} onChange={(e) => { setPlatform(e.target.value); setLimit(PAGE); }}>
              <option value="">TikTok and Instagram</option>{PLATFORMS.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as keyof typeof SORTS)}>
              {(Object.keys(SORTS) as Array<keyof typeof SORTS>).map((k) => <option key={k} value={k}>{SORTS[k]}</option>)}
            </select>
            {otherCount > 0 && (
              <label className="checkbox-line"><input type="checkbox" checked={others} onChange={(e) => setOthers(e.target.checked)} />Show business-owner and off-topic posts ({otherCount})</label>
            )}
          </div>
          {!posts.length ? (
            <div className="cs-empty">
              <strong>{show === "viral" ? "Nothing viral yet" : "Nothing found"}</strong>
              <p className="muted">{paused ? "Try Everything found." :"Try Find more, Past 3 months, broader words, or Everything found."}</p>
            </div>
          ) : (
            <>
              <div className="cs-grid">
                {posts.slice(0, limit).map((p) => <PostCard key={p.id} p={p} onOpen={() => setOpen(p)} />)}
              </div>
              {posts.length > limit && <button type="button" className="button secondary viral-show-more" onClick={() => setLimit(limit + PAGE)}>Show more ({posts.length - limit} left)</button>}
            </>
          )}
        </>
      )}
      {open && (
        <PostLightbox
          p={open} clientId={clientId} source={VIRAL_SOURCE} canChange={data.can_change}
          onClose={() => setOpen(null)}
          onAnalyzed={(analysis) => {
            setOpen(current => current?.id === open.id ? { ...current, analysis } : current);
            setData((prev) => (prev ? { ...prev, posts: prev.posts.map((x) => (x.id === open.id ? { ...x, analysis } : x)) } : prev));
          }}
        />
      )}
    </div>
  );
}

/** Competitors → Viral posts, here too, so all viral content is in one place. */
function CompetitorsView({ clientId }: { clientId: number }) {
  const { data, loading, error, reload } = useApi<{ competitors: Competitor[]; can_change: boolean }>(`/api/competitors?client_id=${clientId}`);
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading && !data) return <p className="muted cs-pad">Loading…</p>;
  if (!data) return null;
  return <PostsView clientId={clientId} competitors={data.competitors} canChange={data.can_change} outliersOnly onCompetitorsChanged={reload} />;
}
